import React, { useState } from 'react';
import { Typography } from 'antd';
import { Lightbulb, X } from 'lucide-react';
import { Bubble, Sender } from '@ant-design/x';
import { motion } from 'framer-motion';
import { ConversationRole } from '@evaltick/shared';
import type { ConversationEntry } from '@/store/interviewStore';
import type { BubbleItemType } from '@ant-design/x';
import styles from './CoachPanel.module.scss';

const { Text } = Typography;

interface CoachPanelProps {
  messages: ConversationEntry[];
  isStreaming: boolean;
  onSendMessage: (message: string) => void;
  canChat: boolean;
  strengthScore: number;
  /** When provided, renders a close button in the header to dismiss the panel. */
  onClose?: () => void;
}

const CIRCUMFERENCE = 2 * Math.PI * 20;

const getStrengthColor = (score: number): string => {
  if (score < 40) return 'var(--color-error)';
  if (score < 70) return 'var(--color-warning)';
  return 'var(--color-success)';
};

const coachAvatar = (
  <div className={styles.coachBubbleAvatar}>
    <Lightbulb style={{ width: '1em', height: '1em' }} />
  </div>
);

const roleConfig = {
  [ConversationRole.Coach]: { placement: 'start' as const, avatar: coachAvatar },
  [ConversationRole.Candidate]: { placement: 'end' as const },
};

export const CoachPanel: React.FC<CoachPanelProps> = ({
  messages,
  isStreaming,
  onSendMessage,
  canChat,
  strengthScore,
  onClose,
}) => {
  const [inputValue, setInputValue] = useState('');

  const items: BubbleItemType[] = messages.map((msg, i) => ({
    key: String(i),
    role: msg.role,
    content: msg.content,
    loading: msg.isStreaming && msg.content === '',
  }));

  const strokeOffset = CIRCUMFERENCE - (strengthScore / 100) * CIRCUMFERENCE;
  const strokeColor = getStrengthColor(strengthScore);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.avatarWrap}>
            <div className={styles.avatar}>
              <Lightbulb style={{ width: '1em', height: '1em' }} />
            </div>
            <motion.div
              className={styles.avatarRing}
              animate={isStreaming ? { opacity: [0.3, 0.7, 0.3], scale: [1, 1.15, 1] } : { opacity: 0.3, scale: 1 }}
              transition={isStreaming ? { repeat: Infinity, duration: 1.8, ease: 'easeInOut' } : {}}
            />
          </div>
          <div className={styles.headerInfo}>
            <Text className={styles.coachName}>Interview Coach</Text>
            <div className={styles.statusRow}>
              <motion.div
                className={styles.statusDot}
                animate={isStreaming ? { scale: [1, 1.5, 1], backgroundColor: ['var(--color-success)', 'var(--color-green-2)', 'var(--color-success)'] } : { scale: 1 }}
                transition={isStreaming ? { repeat: Infinity, duration: 1.2 } : {}}
                style={{ backgroundColor: isStreaming ? 'var(--color-success)' : canChat ? 'var(--color-green-3)' : 'var(--color-border-default)' }}
              />
              <Text className={styles.statusLabel}>
                {isStreaming ? 'Thinking…' : canChat ? 'Ready' : 'Waiting for your answer'}
              </Text>
            </div>
          </div>
        </div>

        <div className={styles.strengthWrap}>
          <Text className={styles.strengthLabel}>Strength</Text>
          <div className={styles.ring}>
            <svg width="48" height="48" viewBox="0 0 48 48">
              <circle
                className={styles.ringBg}
                cx="24"
                cy="24"
                r="20"
                fill="none"
                strokeWidth="4"
              />
              <motion.circle
                cx="24"
                cy="24"
                r="20"
                fill="none"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                animate={{ strokeDashoffset: strokeOffset, stroke: strokeColor }}
                transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
                style={{ transform: 'rotate(-90deg)', transformOrigin: '24px 24px' }}
              />
            </svg>
            <Text className={styles.ringText}>{strengthScore}%</Text>
          </div>
        </div>

        {onClose && (
          <motion.button
            type="button"
            className={styles.headerClose}
            onClick={onClose}
            aria-label="Hide coach"
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
          >
            <X style={{ width: '1em', height: '1em' }} />
          </motion.button>
        )}
      </div>

      <div className={styles.messagesArea}>
        {messages.length === 0 ? (
          <div className={styles.emptyState}>
            <Text className={styles.emptyText}>
              Submit your answer and I'll provide detailed feedback on your response.
            </Text>
          </div>
        ) : (
          <Bubble.List
            items={items}
            role={roleConfig}
            autoScroll
            className={styles.bubbleList}
          />
        )}
      </div>

      <div className={styles.senderArea}>
        <Sender
          placeholder={
            canChat
              ? 'Ask a follow-up question…'
              : 'Submit your answer to start the conversation'
          }
          value={inputValue}
          onChange={setInputValue}
          onSubmit={(msg) => {
            setInputValue('');
            onSendMessage(msg);
          }}
          loading={isStreaming}
          disabled={!canChat || isStreaming}
          className={styles.sender}
        />
      </div>
    </div>
  );
};
